import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useUidsWithDraftMessage } from './ParticipantListItem/useDraftMessage'
import { Filters } from './ParticipantListFilters'
import { countPriorityIndicators, participantMatchesPriorityFilters, PriorityFilter } from './priorityFilterUtils'
import { participantMatchesRoleFilters } from './roleFilterUtils'
import { HomeViewParticipantFragment } from './searchParticipants.generated'

type Participant = HomeViewParticipantFragment

const getAvailableFilterTags = (participants: Participant[]) =>
  _.chain(participants)
    .flatMap((participant) => participant.tags?.map((tag) => tag.name) || [])
    .uniq()
    .value()

const participantHasTag = (participant: Participant, wantedTag: string) =>
  participant.tags?.find((tag) => tag.name === wantedTag)

const filterParticipantsByTags = (participants: Participant[], filters: Filters, matchAnyTag: boolean) => {
  if (filters.tags.length === 0) {
    return participants
  }
  return participants.filter((participant) =>
    matchAnyTag
      ? filters.tags.some((tag) => participantHasTag(participant, tag))
      : filters.tags.every((tag) => participantHasTag(participant, tag))
  )
}

const filterParticipantsByRoles = (participants: Participant[], filters: Filters, coachID: string | undefined) => {
  if (filters.roles.length === 0 || coachID === undefined) {
    return participants
  }
  return participants.filter((participant) => participantMatchesRoleFilters(participant, coachID, filters.roles))
}

const matchesUnreads = (participant: Participant, unreads: boolean) =>
  !unreads || (participant.indicators?.newMessages?.count ?? 0) > 0

const matchesOverdueHq = (participant: Participant, overdueHealthQuestionnaire: boolean) =>
  !overdueHealthQuestionnaire || participant.indicators?.overdueHealthQuestionnaire === true

const matchesPriorities = (participant: Participant, hasDraftMessage: boolean, priorities: Array<PriorityFilter>) =>
  priorities.length === 0 || participantMatchesPriorityFilters(participant, hasDraftMessage, priorities)

// The selected participant always stays, so selecting one doesn't hide it from the list it was picked from.
const filterParticipantsByIndicators = (
  participants: Participant[],
  selectedUid: string | undefined,
  filters: Filters,
  uidsWithDraftMessage: Set<string>
) =>
  participants.filter(
    (participant) =>
      participant.uid === selectedUid ||
      (matchesUnreads(participant, filters.unreads) &&
        matchesOverdueHq(participant, filters.overdueHealthQuestionnaire) &&
        matchesPriorities(participant, uidsWithDraftMessage.has(participant.uid), filters.priorities))
  )

type Options = {
  matchAnyTag?: boolean
  coachID?: string
}

const useParticipantFilters = (
  participants: Participant[],
  selectedUid?: string,
  { matchAnyTag = false, coachID }: Options = {}
) => {
  const [filters, setFilters] = useState<Filters>({
    tags: [],
    roles: [],
    unreads: false,
    overdueHealthQuestionnaire: false,
    priorities: [],
  })
  const uidsWithDraftMessage = useUidsWithDraftMessage(participants.map((participant) => participant.uid))

  const updateFilters = (updatedFilters: Partial<Filters>) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }))
  }

  // The tag and role filters narrow the caseload the priority pills count over; the unreads,
  // overdue HQ and priority filters pick within it, so toggling a pill doesn't shrink the counts.
  const participantsInScope = useMemo(() => {
    const byTags = filterParticipantsByTags(participants, filters, matchAnyTag)
    return filterParticipantsByRoles(byTags, filters, coachID)
  }, [participants, filters, matchAnyTag, coachID])

  const filteredParticipants = useMemo(
    () => filterParticipantsByIndicators(participantsInScope, selectedUid, filters, uidsWithDraftMessage),
    [participantsInScope, selectedUid, filters, uidsWithDraftMessage]
  )

  const priorityCounts = useMemo(
    () => countPriorityIndicators(participantsInScope, uidsWithDraftMessage),
    [participantsInScope, uidsWithDraftMessage]
  )

  return {
    availableFilterTags: useMemo(() => getAvailableFilterTags(participants), [participants]),
    filteredParticipants,
    filters,
    isFiltered: Object.values(filters).some((value) => (Array.isArray(value) ? value.length > 0 : value)),
    participantsInScope,
    priorityCounts,
    uidsWithDraftMessage,
    updateFilters,
  }
}

export default useParticipantFilters
